import {
  Target,
  ShieldCheck,
  Zap,
  Award,
  HeartHandshake,
  Clock,
  BadgeCheck,
  Eye,
  Cpu,
  Users,
} from "lucide-react";
import { Reveal, Overline } from "./Reveal";

const VALUES = [
  { icon: BadgeCheck, title: "Excelência Operacional", text: "Processos padronizados e melhoria contínua em todas as etapas." },
  { icon: ShieldCheck, title: "Segurança em Primeiro Lugar", text: "Protocolos rigorosos para clientes, colaboradores e meio ambiente." },
  { icon: Eye, title: "Transparência e Ética", text: "Comunicação clara sobre procedimentos, produtos e resultados esperados." },
  { icon: Cpu, title: "Inovação Tecnológica", text: "Investimento constante em equipamentos modernos e técnicas avançadas." },
  { icon: Users, title: "Valorização de Pessoas", text: "Desenvolvimento contínuo da equipe e relacionamento de confiança com clientes." },
];

const PILLARS = [
  { icon: ShieldCheck, title: "Segurança Total", text: "Métodos seguros certificados, produtos registrados na ANVISA e EPIs completos" },
  { icon: Zap, title: "Alta Eficiência", text: "Resultados rápidos e duradouros com tecnologia de ponta" },
  { icon: Award, title: "Qualidade Premium", text: "Produtos de alta tecnologia e equipamentos modernos" },
  { icon: HeartHandshake, title: "Confiança", text: "Mais de 11 anos de experiência e milhares de clientes satisfeitos" },
  { icon: Clock, title: "Atendimento Ágil", text: "Atendimento emergencial 24h e agendamento flexível" },
  { icon: BadgeCheck, title: "Garantia Estendida", text: "Garantia de serviço e acompanhamento pós-tratamento" },
];

export const About = () => (
  <section id="sobre" className="relative py-16 sm:py-24 lg:py-32 overflow-hidden">
    <div className="absolute top-20 -right-32 h-[400px] w-[400px] rounded-full bg-gold/10 blur-[140px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Overline>A Gold Service</Overline>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-2xl text-balance">
          Missão, valores & <span className="gold-text">diferencial</span>
        </h2>
      </Reveal>

      <div className="mt-10 grid lg:grid-cols-12 gap-5 sm:gap-6">
        <Reveal className="lg:col-span-5">
          <div data-testid="mission-card" className="gold-border-gradient rounded-3xl bg-card p-6 sm:p-8 h-full">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gold/15 text-gold">
              <Target className="h-5 w-5" />
            </span>
            <h3 className="mt-4 font-display text-xl sm:text-2xl font-bold">Nossa Missão</h3>
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-mist">
              Proporcionar ambientes saudáveis, seguros e livres de pragas
              urbanas através de soluções integradas, tecnologia de ponta e
              equipe altamente qualificada. Nosso compromisso é superar as
              expectativas de nossos clientes, oferecendo serviços de
              excelência com responsabilidade ambiental e social.
            </p>
          </div>
        </Reveal>

        <Reveal className="lg:col-span-7" delay={0.1}>
          <div className="rounded-3xl border border-white/10 bg-card/60 p-6 sm:p-8 h-full">
            <h3 className="font-display text-xl sm:text-2xl font-bold">Nossos Valores</h3>
            <ul className="mt-5 space-y-4">
              {VALUES.map((v, i) => (
                <li key={v.title} data-testid={`value-item-${i}`} className="flex gap-4 group">
                  <span className="shrink-0 mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-xl border border-gold/30 bg-gold/10 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-ink">
                    <v.icon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-zinc-100">{v.title}</p>
                    <p className="text-xs sm:text-sm text-mist mt-0.5">{v.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1} className="mt-5 sm:mt-6">
        <div data-testid="differential-block" className="rounded-3xl border border-gold/25 bg-gradient-to-br from-coal to-card p-6 sm:p-10">
          <h3 className="font-display text-xl sm:text-2xl font-bold">
            Nosso <span className="gold-text">Diferencial</span>
          </h3>
          <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm sm:text-base leading-relaxed text-mist">
            <p>
              A Gold Service se destaca no mercado pela combinação única de
              experiência técnica, certificações profissionais e sistema de
              gestão integrada. Não somos apenas uma dedetizadora, somos
              especialistas em controle integrado de ambientes com visão
              holística e sustentável.
            </p>
            <p>
              Oferecemos diagnóstico preciso, tratamento personalizado,
              acompanhamento pós-serviço e garantia estendida. Nossos técnicos
              são certificados nas principais normas de segurança do trabalho
              e recebem treinamento contínuo sobre novas técnicas, produtos e
              legislação vigente.
            </p>
          </div>
        </div>
      </Reveal>

      <div className="mt-5 sm:mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {PILLARS.map((p, i) => (
          <Reveal key={p.title} delay={Math.min(i * 0.07, 0.35)}>
            <div
              data-testid={`pillar-card-${i}`}
              className="group h-full rounded-2xl border border-white/10 bg-card/60 p-5 transition-all duration-500 hover:border-gold/40 hover:-translate-y-1 hover:shadow-[0_16px_50px_-20px_rgba(212,175,55,0.35)]"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gold/15 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-ink">
                <p.icon className="h-5 w-5" />
              </span>
              <p className="mt-3 font-display font-bold text-zinc-50">{p.title}</p>
              <p className="mt-1 text-xs sm:text-sm leading-relaxed text-mist">{p.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
